'use client'
import { ButtonProps } from '@chakra-ui/react'
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Image,
  useDisclosure,
  UseDisclosureProps,
} from '@core/ui-elements'
import {
  ForwardedRef,
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
} from 'react'

import { LoadingScreen } from '../loading-screen'
import { StatCard } from '../stats-cards'

export interface IBottomSheet {
  header?: {
    content?: string
    label?: string
  }
  image?: {
    src: string
    alt: string
  }
  children?: ReactNode
  footerButtons?: ButtonProps[]
  color?: 'red' | 'default' | 'green' | 'yellow'
  canClose?: boolean
  defaultIsOpen?: boolean
  handleOnClose?: (index?: number) => void
  isLoading?: boolean
  isLoadingContent?: boolean
}

const BottomSheet = forwardRef(
  (
    {
      header,
      children,
      footerButtons,
      color = 'default',
      canClose = true,
      defaultIsOpen = false,
      handleOnClose: onClosePropFunc,
      image,
      isLoading = false,
      isLoadingContent = false,
    }: IBottomSheet,
    ref: ForwardedRef<UseDisclosureProps | undefined>
  ) => {
    const { isOpen, onOpen, onClose } = useDisclosure()

    useImperativeHandle(ref, () => ({
      onOpen,
      onClose,
    }))

    const colorScheme = {
      default: {
        backgroundColor: 'white',
        borderBottomColor: 'gray.200',
      },
      red: {
        backgroundColor: 'red.100',
        borderBottomColor: 'red.500',
      },
      green: {
        backgroundColor: 'green.100',
        borderBottomColor: 'green.500',
      },
      yellow: {
        backgroundColor: 'orange.100',
        borderBottomColor: 'yellow.500',
      },
    }

    const handleOnClose = useCallback(() => {
      onClosePropFunc?.()
      onClose()
    }, [onClose, onClosePropFunc])

    return (
      <Drawer
        autoFocus={true}
        isOpen={defaultIsOpen ? defaultIsOpen : isOpen}
        placement={'bottom'}
        returnFocusOnClose={true}
        size={'full'}
        trapFocus={true}
        variant={'fullBottom'}
        onClose={handleOnClose}
      >
        <DrawerOverlay />
        <DrawerContent>
          {header ? (
            <DrawerHeader {...colorScheme[color]}>
              <Flex>
                <StatCard content={header.content} label={header.label} />
              </Flex>
              {canClose ? <DrawerCloseButton size="md" /> : null}
            </DrawerHeader>
          ) : null}

          {isLoadingContent ? (
            <DrawerBody>
              <LoadingScreen />
            </DrawerBody>
          ) : (
            <DrawerBody>
              {image ? (
                <Flex justifyContent="center">
                  <Image
                    alt={image.alt}
                    src={image.src}
                    style={{ width: '12.5rem', height: '12.5rem' }}
                  />
                </Flex>
              ) : null}

              {children}
            </DrawerBody>
          )}

          {footerButtons && footerButtons.length > 0 ? (
            <DrawerFooter>
              <Box margin={0} padding={0} width="100%">
                {footerButtons.map((footerButton, index) => (
                  <Button
                    key={index}
                    isLoading={isLoading}
                    marginY="0.25rem"
                    width="100%"
                    {...footerButton}
                  >
                    {footerButton.children}
                  </Button>
                ))}
              </Box>
            </DrawerFooter>
          ) : null}
        </DrawerContent>
      </Drawer>
    )
  }
)

BottomSheet.displayName = 'BottomSheet'

export default BottomSheet
